/**
 * Lightweight analytics wrapper.
 *
 * Currently supports Google Analytics 4 (gtag.js). Activates only when
 * REACT_APP_GA_MEASUREMENT_ID is set. Respects the browser's Do Not Track
 * signal — if DNT is enabled, no script is loaded and no events are sent.
 *
 * To swap providers (Plausible, Fathom, etc.), replace the loadScript +
 * pageview/event implementations below; the public API (initAnalytics,
 * trackPageview, trackEvent) is stable.
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

let initialized = false;

function dntEnabled() {
  if (typeof navigator === 'undefined') return false;
  // Treat any "1" or "yes" value from any of the DNT properties as opt-out
  const dnt =
    navigator.doNotTrack ||
    window.doNotTrack ||
    navigator.msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
}

function loadGtag(measurementId) {
  if (typeof document === 'undefined') return;

  // gtag stub installed before the script loads so calls are queued
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  // anonymize_ip and ad_storage denied by default for GDPR-friendliness
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  });
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    send_page_view: false, // we'll send page views manually on route change
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

export function initAnalytics() {
  if (initialized) return;
  if (!GA_MEASUREMENT_ID) return;
  if (dntEnabled()) return;

  loadGtag(GA_MEASUREMENT_ID);
  initialized = true;
}

export function trackPageview(path, title) {
  if (!initialized || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
  });
}

export function trackEvent(name, params = {}) {
  if (!initialized || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}

export function isAnalyticsEnabled() {
  return Boolean(GA_MEASUREMENT_ID) && !dntEnabled();
}
